/**
 * Телефоны Квестгида файлом с компьютера ЕО (27.10.2026): разбор kvestgid-phones.json в браузере и деление на части.
 * Формат файла: {version: 1, kind: "kvestgid-phones", rows: [[ext_id (24 hex), phone_hash (64 hex)], …], note?}. В бандле и в git
 * телефонов нет: файл читается только здесь, в памяти страницы, и уходит на сервер частями по PHONES_PART строк
 * (POST /api/eo/kvestgid-origin/phones, один параметр json_each на часть). Сервер возвращает только счётчики.
 */

/** Строк в части: как у плана (сервер принимает до 1000). */
export const PHONES_PART = 800;
/** Больше — это не наш файл (в нём ~3 тыс. строк, ~300 КБ). */
const MAX_BYTES = 5 * 1024 * 1024;
const EXT = /^[0-9a-f]{24}$/;
const HASH = /^[0-9a-f]{64}$/;
const KEYS = new Set(["version", "kind", "rows", "note"]);

export type PhoneRow = [string, string];
/** games_mismatch — игр с другим телефоном брони, чем в файле (сервер их не меняет; только число для сверки). */
export interface PhonesResult { rows: number; stubs: number; history: number; games: number; games_mismatch?: number }

/** Текст файла → строки или ошибка (без значений строк в тексте). */
export function parsePhonesFile(text: string): { rows: PhoneRow[] } | { error: string } {
  if (text.length > MAX_BYTES) return { error: "Файл слишком большой — это не файл телефонов Квестгида" };
  let data: unknown;
  try { data = JSON.parse(text); } catch { return { error: "Файл не читается как JSON" }; }
  if (!data || typeof data !== "object" || Array.isArray(data)) return { error: "Это не файл телефонов Квестгида" };
  const d = data as Record<string, unknown>;
  if (d.kind !== "kvestgid-phones" || d.version !== 1 || Object.keys(d).some((k) => !KEYS.has(k))) {
    return { error: "Это не файл телефонов Квестгида (нужны kind «kvestgid-phones» и version 1)" };
  }
  if (!Array.isArray(d.rows) || d.rows.length === 0) return { error: "В файле нет строк" };
  const seen = new Set<string>();
  for (const [i, r] of (d.rows as unknown[]).entries()) {
    if (!Array.isArray(r) || r.length !== 2 || typeof r[0] !== "string" || typeof r[1] !== "string" || !EXT.test(r[0]) || !HASH.test(r[1])) {
      return { error: `Строка ${i + 1} не той формы — нужна пара [ext_id, хеш телефона]` };
    }
    if (seen.has(r[0])) return { error: `Строка ${i + 1}: ext_id повторяется` };
    seen.add(r[0]);
  }
  return { rows: d.rows as PhoneRow[] };
}

/** Части по `size` строк. */
export function phoneChunks(rows: readonly PhoneRow[], size = PHONES_PART): PhoneRow[][] {
  const out: PhoneRow[][] = [];
  for (let i = 0; i < rows.length; i += size) out.push(rows.slice(i, i + size));
  return out;
}

/** Сумма ответов сервера по частям. */
export function sumPhones(parts: readonly PhonesResult[]): PhonesResult {
  return parts.reduce((a, p) => ({ rows: a.rows + (p.rows || 0), stubs: a.stubs + (p.stubs || 0), history: a.history + (p.history || 0), games: a.games + (p.games || 0),
    games_mismatch: (a.games_mismatch ?? 0) + (p.games_mismatch || 0) }), { rows: 0, stubs: 0, history: 0, games: 0, games_mismatch: 0 });
}

const n = (v: number) => v.toLocaleString("ru-RU");
/** Итог для панели: «Строк 3 078: записано в историю 2 700, в игры без сумм 300; заглушек 2». Остальные — уже заполнены или не наши. */
export function phonesSummary(t: PhonesResult): string {
  const tail = t.stubs ? `; номеров-заглушек отброшено ${n(t.stubs)}` : "";
  const diff = t.games_mismatch ? ` У ${n(t.games_mismatch)} игр без сумм телефон брони не тот, что в файле, — их не меняли.` : "";
  return `Строк ${n(t.rows)}: записано в историю ${n(t.history)}, в игры без сумм ${n(t.games)}${tail}. Остальные уже были заполнены или не из загрузки Квестгида.${diff}`;
}
